import {
  useQuery, useMutation, useQueryClient,
} from '@tanstack/react-query';
import {
  getStorageLocations,
  getTemperatureHistory,
  updateAlertConfig,
  updateThresholds,
  acknowledgeAlert,
} from '../api/storage-client';
import type {
  AlertConfig, TemperatureThresholds,
} from '../types/storage';

export const STORAGE_LOCATIONS_QUERY_KEY = 'storage-locations';
export const TEMPERATURE_HISTORY_QUERY_KEY = 'temperature-history';

export function useStorageLocations(siteId: string) {
  return useQuery({
    queryKey: [STORAGE_LOCATIONS_QUERY_KEY, siteId],
    queryFn: () => getStorageLocations(siteId),
    refetchInterval: 30000,
  });
}

export function useTemperatureHistory(
  siteId: string,
  locationId: string | null,
  hours = 24
) {
  return useQuery({
    queryKey: [TEMPERATURE_HISTORY_QUERY_KEY, siteId, locationId, hours],
    queryFn: () => getTemperatureHistory(siteId, locationId as string, hours),
    enabled: Boolean(locationId),
    refetchInterval: 60000,
  });
}

export function useUpdateAlertConfig(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationId, config 
    }: {
      locationId: string;
      config: AlertConfig 
    }) =>
      updateAlertConfig(siteId, locationId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [STORAGE_LOCATIONS_QUERY_KEY, siteId],});
    },
  });
}

export function useUpdateThresholds(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationId, thresholds 
    }: {
      locationId: string;
      thresholds: TemperatureThresholds 
    }) =>
      updateThresholds(siteId, locationId, thresholds),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [STORAGE_LOCATIONS_QUERY_KEY, siteId],});
    },
  });
}

export function useAcknowledgeAlert(siteId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      locationId, alertId 
    }: {
      locationId: string;
      alertId: string 
    }) =>
      acknowledgeAlert(siteId, locationId, alertId),
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({queryKey: [TEMPERATURE_HISTORY_QUERY_KEY, siteId, locationId],});
    },
  });
}
